import { useEffect, useRef, useState } from 'react';
import { Mail, X, Send, CheckCircle, Linkedin, Instagram, Facebook, Twitter, BookOpen } from 'lucide-react';
import { siteData } from '../data/mock';

const FORMSPREE_URL = 'https://formspree.io/f/mjgeavya';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => {
          onClose();
          setTimeout(() => setSubmitted(false), 300);
        }, 3000);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full max-w-lg bg-[#12141a] border border-[rgba(0,212,255,0.12)] rounded-2xl shadow-2xl overflow-hidden animate-modal-in"
        style={{
          boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 80px rgba(0,212,255,0.05)',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 text-[#555] transition-colors duration-200 hover:text-[#e8e6e1]"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="p-6 md:p-10">
          {submitted ? (
            /* Success State */
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[rgba(0,212,255,0.1)] flex items-center justify-center mb-6">
                <CheckCircle size={32} className="text-[#00d4ff]" />
              </div>
              <h3 className="font-montserrat font-700 text-xl text-[#e8e6e1] mb-3">
                Message Sent
              </h3>
              <p className="font-cardo text-base text-[#888680] leading-relaxed max-w-sm">
                Thank you for reaching out. I'll get back to you shortly.
              </p>
            </div>
          ) : (
            /* Form State */
            <>
              <div className="mb-8">
                <p className="font-montserrat text-xs tracking-[0.3em] uppercase text-[#00d4ff] mb-2">
                  Get in Touch
                </p>
                <h3 className="font-montserrat font-800 text-2xl md:text-3xl uppercase text-[#e8e6e1] leading-[0.9]">
                  Contact GS Rylan
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Full Name */}
                <div>
                  <label className="block font-montserrat text-xs tracking-[0.15em] uppercase text-[#888680] mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg px-4 py-3 font-cardo text-base text-[#e8e6e1] placeholder-[#444] outline-none transition-all duration-300 focus:border-[rgba(0,212,255,0.3)] focus:bg-[rgba(0,212,255,0.02)]"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block font-montserrat text-xs tracking-[0.15em] uppercase text-[#888680] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg px-4 py-3 font-cardo text-base text-[#e8e6e1] placeholder-[#444] outline-none transition-all duration-300 focus:border-[rgba(0,212,255,0.3)] focus:bg-[rgba(0,212,255,0.02)]"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block font-montserrat text-xs tracking-[0.15em] uppercase text-[#888680] mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Your message..."
                    className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg px-4 py-3 font-cardo text-base text-[#e8e6e1] placeholder-[#444] outline-none transition-all duration-300 focus:border-[rgba(0,212,255,0.3)] focus:bg-[rgba(0,212,255,0.02)] resize-none"
                  />
                </div>

                {/* Error */}
                {error && (
                  <p className="font-montserrat text-xs text-red-400">{error}</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-3 w-full font-montserrat font-semibold text-sm tracking-wider uppercase px-8 py-4 rounded-full bg-[#00d4ff] text-[#0d0f12] border-none cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(0,212,255,0.2)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {submitting ? (
                    <span className="inline-block w-5 h-5 border-2 border-[#0d0f12]/30 border-t-[#0d0f12] rounded-full animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const socialLinks = [
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/gsrylan', icon: Linkedin },
  { name: 'Goodreads', href: 'https://www.goodreads.com/gsrylan', icon: BookOpen },
  { name: 'X', href: 'https://x.com/gsrylan', icon: Twitter },
  { name: 'Instagram', href: 'https://www.instagram.com/gsrylan/', icon: Instagram },
  { name: 'Facebook', href: 'https://www.facebook.com/gsrylan.author', icon: Facebook },
];

export const ContactSection = () => {
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section
        id="contact"
        ref={sectionRef}
        className="relative py-20 md:py-40"
        style={{ background: '#0d0f12' }}
      >
        {/* Top separator */}
        <div className="absolute top-0 left-5 right-5 md:left-10 md:right-10 h-px bg-gradient-to-r from-transparent via-[rgba(0,212,255,0.15)] to-transparent" />

        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
            {/* Left Column: Contact */}
            <div
              className={`lg:w-1/2 transition-all duration-1000 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <p className="font-montserrat text-xs tracking-[0.3em] uppercase text-[#00d4ff] mb-4">
                Get in Touch
              </p>
              <h2 className="font-montserrat font-800 text-3xl md:text-5xl uppercase text-[#e8e6e1] leading-[0.85] mb-6 md:mb-8">
                Contact
              </h2>
              <p className="font-cardo text-base md:text-lg text-[#888680] leading-relaxed mb-8 md:mb-12">
                For media, rights inquiries, and professional correspondence:
              </p>

              <button
                onClick={() => setModalOpen(true)}
                className="group inline-flex items-center gap-3 md:gap-4 py-4 md:py-5 px-6 md:px-10 rounded-full bg-[#00d4ff] text-[#0d0f12] font-montserrat font-semibold text-sm tracking-wider uppercase cursor-pointer border-none transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(0,212,255,0.2)] active:scale-[0.97]"
              >
                <Mail size={18} />
                Contact GS Rylan
              </button>
            </div>

            {/* Right Column: Social Links */}
            <div
              className={`lg:w-1/2 transition-all duration-1000 delay-200 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <p className="font-montserrat text-xs tracking-[0.3em] uppercase text-[#00d4ff] mb-4">
                Follow
              </p>
              <h3 className="font-montserrat font-700 text-2xl md:text-3xl uppercase text-[#e8e6e1] leading-[0.9] mb-10 md:mb-12">
                Connect With<br />
                <span className="text-[#c4a962]">GS Rylan</span>
              </h3>

              {/* Desktop: Horizontal row */}
              <div className="hidden md:flex items-start justify-start gap-10">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex flex-col items-center gap-3 no-underline"
                    >
                      <div className="w-12 h-12 rounded-full border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] flex items-center justify-center transition-all duration-300 group-hover:border-[rgba(0,212,255,0.3)] group-hover:bg-[rgba(0,212,255,0.05)]">
                        <Icon size={20} className="text-[#888680] transition-colors duration-300 group-hover:text-[#e8e6e1]" />
                      </div>
                      <span className="font-montserrat text-[11px] tracking-[0.1em] uppercase text-[#555] transition-colors duration-300 group-hover:text-[#e8e6e1]">
                        {social.name}
                      </span>
                    </a>
                  );
                })}
              </div>

              {/* Mobile: Vertical stack */}
              <div className="flex md:hidden flex-col items-center gap-6">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex flex-col items-center gap-2.5 no-underline py-2"
                    >
                      <div className="w-14 h-14 rounded-full border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] flex items-center justify-center transition-all duration-300 group-hover:border-[rgba(0,212,255,0.3)] group-hover:bg-[rgba(0,212,255,0.05)]">
                        <Icon size={22} className="text-[#888680] transition-colors duration-300 group-hover:text-[#e8e6e1]" />
                      </div>
                      <span className="font-montserrat text-xs tracking-[0.1em] uppercase text-[#555] transition-colors duration-300 group-hover:text-[#e8e6e1]">
                        {social.name}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export const Footer = () => {
  return (
    <footer
      className="relative py-10 md:py-16"
      style={{ background: '#080a0d' }}
    >
      {/* Top separator */}
      <div className="absolute top-0 left-5 right-5 md:left-10 md:right-10 h-px bg-[rgba(255,255,255,0.04)]" />

      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-8">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <img
              src={siteData.images.logo}
              alt="G.S. Rylan"
              className="h-10 w-10 object-contain opacity-50"
            />
            <span className="font-montserrat text-sm text-[#555] tracking-wider uppercase">
              G.S. Rylan
            </span>
          </div>

          {/* Nav Links */}
          <nav className="flex flex-wrap gap-6">
            {siteData.nav.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="font-montserrat text-xs tracking-[0.15em] uppercase text-[#444] transition-colors duration-300 hover:text-[#888] no-underline"
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Copyright */}
          <p className="font-montserrat text-xs text-[#333]">
            &copy; {new Date().getFullYear()} G.S. Rylan. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
